import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ListingDraftInput } from '@qm/shared';
import { useTranslation } from 'react-i18next';
import {
  api,
  qs,
  type AttributeView,
  type CategoryNode,
  type ChatHeader,
  type ChatListItem,
  type ContactOptions,
  type ListingPromotionState,
  type PromotionTariffView,
  type ListingCardData,
  type ListingDetail,
  type LocationNode,
  type Me,
  type MyReviewState,
  type Page,
  type ReviewItem,
  type SellerProfile,
} from './api';

const useLocale = () => useTranslation().i18n.language as 'uz' | 'kaa' | 'ru';

export interface SearchParams {
  q?: string;
  categoryId?: string;
  locationId?: string;
  priceMin?: string;
  priceMax?: string;
  condition?: 'new' | 'used';
  withPhotos?: boolean;
  sort?: 'newest' | 'price_asc' | 'price_desc' | 'relevance';
  attrs?: Record<string, string>;
  negotiable?: boolean;
  sellerType?: 'private' | 'business';
}

export function searchToQuery(p: SearchParams, cursor?: string | null, limit = 20) {
  const attrs = Object.fromEntries(Object.entries(p.attrs ?? {}).map(([k, v]) => [`attr.${k}`, v]));
  return qs({
    q: p.q,
    categoryId: p.categoryId,
    locationId: p.locationId,
    priceMin: p.priceMin,
    priceMax: p.priceMax,
    condition: p.condition,
    withPhotos: p.withPhotos ? 'true' : undefined,
    negotiable: p.negotiable ? 'true' : undefined,
    sellerType: p.sellerType,
    sort: p.sort,
    cursor: cursor ?? undefined,
    limit,
    ...attrs,
  });
}

export function useCategories() {
  const locale = useLocale();
  return useQuery({
    queryKey: ['categories', locale],
    queryFn: () => api<{ items: CategoryNode[] }>(`/categories${qs({ locale })}`).then((r) => r.items),
    staleTime: 10 * 60_000,
  });
}

export function useCategoryAttributes(categoryId: string | null | undefined) {
  const locale = useLocale();
  return useQuery({
    queryKey: ['attributes', categoryId, locale],
    queryFn: () => api<{ items: AttributeView[] }>(`/categories/${categoryId}/attributes${qs({ locale })}`).then((r) => r.items),
    enabled: Boolean(categoryId),
    staleTime: 10 * 60_000,
  });
}

export function useLocations() {
  const locale = useLocale();
  return useQuery({
    queryKey: ['locations', locale],
    queryFn: () => api<{ items: LocationNode[] }>(`/locations${qs({ locale })}`).then((r) => r.items),
    staleTime: 10 * 60_000,
  });
}

export function useBanners() {
  const locale = useLocale();
  return useQuery({
    queryKey: ['banners', locale],
    queryFn: () =>
      api<{ items: { id: string; title: string; imageUrl: string; linkUrl: string | null }[] }>(`/banners${qs({ locale })}`).then(
        (r) => r.items,
      ),
    staleTime: 5 * 60_000,
  });
}

export function useSearch(params: SearchParams, enabled = true, limit = 20) {
  const locale = useLocale();
  return useInfiniteQuery({
    queryKey: ['search', params, locale, limit],
    queryFn: ({ pageParam }) => api<Page<ListingCardData>>(`/listings${searchToQuery(params, pageParam, limit)}&locale=${locale}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? null,
    enabled,
  });
}

/**
 * VIP-объявления по тем же фильтрам — показываются отдельной строкой над обычной выдачей
 * (сама выдача и её пагинация не меняются). Сервер вернёт пусто, если продвижение выключено.
 */
export function useVipStrip(params: SearchParams, enabled = true) {
  const locale = useLocale();
  return useQuery({
    queryKey: ['search-vip', params, locale],
    queryFn: () =>
      api<Page<ListingCardData>>(`/listings${searchToQuery({ ...params, sort: undefined }, null, 2)}&vipOnly=true&locale=${locale}`).then((r) => r.items),
    enabled,
    staleTime: 60_000,
  });
}

export function usePopular(locationId?: string | null) {
  const locale = useLocale();
  return useQuery({
    queryKey: ['popular', locationId, locale],
    queryFn: () => api<{ items: ListingCardData[] }>(`/listings/popular${qs({ locationId, locale })}`).then((r) => r.items),
  });
}

export function usePromoted(locationId?: string | null) {
  const locale = useLocale();
  return useQuery({
    queryKey: ['promoted', locationId, locale],
    queryFn: () => api<{ items: ListingCardData[] }>(`/listings/promoted${qs({ locationId, locale })}`).then((r) => r.items),
    staleTime: 60_000,
  });
}

export function useListing(id: string | undefined) {
  const locale = useLocale();
  return useQuery({
    queryKey: ['listing', id, locale],
    queryFn: () => api<ListingDetail>(`/listings/${id}${qs({ locale })}`),
    enabled: Boolean(id),
  });
}

export function useSimilar(id: string | undefined) {
  const locale = useLocale();
  return useQuery({
    queryKey: ['similar', id, locale],
    queryFn: () => api<{ items: ListingCardData[] }>(`/listings/${id}/similar${qs({ locale })}`).then((r) => r.items),
    enabled: Boolean(id),
  });
}

export function useMyListings(status: string) {
  return useQuery({
    queryKey: ['my-listings', status],
    queryFn: () => api<{ items: ListingCardData[]; counts: Record<string, number> }>(`/listings/mine${qs({ status })}`),
  });
}

export function useFavorites() {
  return useQuery({
    queryKey: ['favorites'],
    queryFn: () => api<{ items: ListingCardData[] }>('/favorites').then((r) => r.items),
  });
}

/** Переключение избранного с оптимистичным обновлением и откатом при ошибке. */
export function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      api<{ isFavorite: boolean }>(`/favorites/${id}`, { method: value ? 'PUT' : 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['favorites'] });
      void qc.invalidateQueries({ queryKey: ['listing'] });
      void qc.invalidateQueries({ queryKey: ['search'] });
      void qc.invalidateQueries({ queryKey: ['popular'] });
    },
  });
}

export function useContactOptions(listingId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['contact', listingId],
    queryFn: () => api<ContactOptions>(`/listings/${listingId}/contact`),
    enabled: Boolean(listingId) && enabled,
    staleTime: 60_000,
  });
}

export function useRecentSearches() {
  return useQuery({
    queryKey: ['recent-searches'],
    queryFn: () => api<{ items: string[] }>('/users/me/searches').then((r) => r.items),
  });
}

export function useInquiries(box: 'received' | 'sent') {
  return useQuery({
    queryKey: ['inquiries', box],
    queryFn: () =>
      api<{
        items: {
          id: string;
          listing: { id: string; title: string; status: string };
          counterpartName: string;
          message: string | null;
          buyerUsername?: string | null;
          buyerPhone?: string | null;
          createdAt: string;
        }[];
      }>(`/contacts/inquiries${qs({ box })}`).then((r) => r.items),
  });
}

export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () =>
      api<{
        unread: number;
        items: { id: string; type: string; payload: Record<string, string | null>; createdAt: string; read: boolean; delivery: string }[];
      }>('/notifications'),
    refetchInterval: 60_000,
  });
}

export function useSaveDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string | null; data: ListingDraftInput }) =>
      id ? api<ListingDetail>(`/listings/${id}`, { method: 'PATCH', json: data }) : api<ListingDetail>('/listings', { method: 'POST', json: data }),
    onSuccess: (l) => {
      void qc.invalidateQueries({ queryKey: ['listing', l.id] });
      void qc.invalidateQueries({ queryKey: ['my-listings'] });
    },
  });
}

export function useCategoryCounts() {
  return useQuery({
    queryKey: ['category-counts'],
    queryFn: () => api<{ counts: Record<string, number> }>('/categories/counts'),
    staleTime: 60_000,
  });
}

export function usePopularSearches() {
  return useQuery({
    queryKey: ['popular-searches'],
    queryFn: () => api<{ items: string[] }>('/search/popular'),
    staleTime: 5 * 60_000,
  });
}

/** «Показать N объявлений» в фильтрах: число найденных по черновику фильтров. */
export function useSearchCount(params: SearchParams, enabled: boolean) {
  return useQuery({
    queryKey: ['search-count', params],
    queryFn: () => api<{ count: number; capped: boolean }>(`/listings/count${searchToQuery(params)}`),
    enabled,
    staleTime: 30_000,
  });
}

export function useSeller(id: string | undefined) {
  return useQuery({
    queryKey: ['seller', id],
    queryFn: () => api<SellerProfile>(`/users/${id}`),
    enabled: Boolean(id),
  });
}

export function useSellerListings(id: string | undefined) {
  const locale = useLocale();
  return useInfiniteQuery({
    queryKey: ['seller-listings', id, locale],
    queryFn: ({ pageParam }) => api<{ items: ListingCardData[]; nextOffset: number | null }>(`/users/${id}/listings${qs({ offset: pageParam })}`),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextOffset ?? undefined,
    enabled: Boolean(id),
  });
}

export function useSellerReviews(id: string | undefined) {
  return useInfiniteQuery({
    queryKey: ['seller-reviews', id],
    queryFn: ({ pageParam }) => api<{ items: ReviewItem[]; nextOffset: number | null }>(`/users/${id}/reviews${qs({ offset: pageParam })}`),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextOffset ?? undefined,
    enabled: Boolean(id),
  });
}

/** poll — пока отзыв оставить нельзя (продавец ещё не ответил), проверять раз в 30 с (открытый чат). */
export function useMyReview(sellerId: string | undefined, enabled = true, poll = false) {
  return useQuery({
    queryKey: ['my-review', sellerId],
    queryFn: () => api<MyReviewState>(`/users/${sellerId}/my-review`),
    enabled: Boolean(sellerId) && enabled,
    refetchInterval: poll ? (q) => (q.state.data?.eligible ? false : 30_000) : false,
  });
}

export function useSaveReview(sellerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { rating: number; text: string } | null): Promise<MyReviewState | null> => {
      if (v) return api<MyReviewState>(`/users/${sellerId}/my-review`, { method: 'PUT', json: v });
      await api<void>(`/users/${sellerId}/my-review`, { method: 'DELETE' });
      return null;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['my-review', sellerId] });
      void qc.invalidateQueries({ queryKey: ['seller-reviews', sellerId] });
      void qc.invalidateQueries({ queryKey: ['seller', sellerId] });
      void qc.invalidateQueries({ queryKey: ['listing'] });
    },
  });
}

export function useListingAction() {
  const qc = useQueryClient();
  return useMutation({
    // Удаление ничего не возвращает (204) — тогда результат null.
    mutationFn: async ({ id, action }: { id: string; action: 'submit' | 'archive' | 'sold' | 'republish' | 'renew' | 'delete' }): Promise<ListingDetail | null> => {
      if (action === 'delete') {
        await api<void>(`/listings/${id}`, { method: 'DELETE' });
        return null;
      }
      return api<ListingDetail>(`/listings/${id}/${action}`, { method: 'POST' });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['my-listings'] });
      void qc.invalidateQueries({ queryKey: ['listing'] });
      void qc.invalidateQueries({ queryKey: ['search'] });
    },
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ path, method, json }: { path: string; method: 'PATCH' | 'PUT' | 'POST' | 'DELETE'; json?: unknown }) =>
      api<Me>(path, { method, json }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['search'] });
      void qc.invalidateQueries({ queryKey: ['popular'] });
    },
  });
}

// ---------------- чат

export function useChats(enabled = true) {
  return useQuery({
    queryKey: ['chats'],
    queryFn: () => api<{ items: ChatListItem[] }>('/chats').then((r) => r.items),
    enabled,
    refetchInterval: 15_000,
  });
}

/** Непрочитанные сообщения — значок в нижнем меню. Опрос раз в 30 с (и при возврате в приложение). */
export function useChatUnread() {
  return useQuery({
    queryKey: ['chats-unread'],
    queryFn: () => api<{ unread: number; conversations: number }>('/chats/unread'),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

export function useChatHeader(id: string | undefined) {
  return useQuery({
    queryKey: ['chat', id],
    queryFn: () => api<ChatHeader>(`/chats/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

/** Начать переписку по объявлению (или открыть существующую). */
export function useStartChat() {
  return useMutation({
    mutationFn: (listingId: string) => api<{ id: string; created: boolean }>('/chats', { method: 'POST', json: { listingId } }),
  });
}

// ---------------- продвижение

export function usePromotionTariffs(enabled = true) {
  return useQuery({
    queryKey: ['promotion-tariffs'],
    queryFn: () => api<{ enabled: boolean; currency: string; items: PromotionTariffView[] }>('/promotions/tariffs'),
    enabled,
    // Короткий кэш: после включения оплаты в админке кнопка появляется без перезапуска приложения.
    staleTime: 30_000,
  });
}

export function useListingPromotion(listingId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['listing-promotion', listingId],
    queryFn: () => api<ListingPromotionState>(`/listings/${listingId}/promotion`),
    enabled: Boolean(listingId) && enabled,
  });
}
